import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import React from 'react'
import { getTransactionStatusChipColor } from '../helpers'
import { EmptyTransactionsAlert, ExpandedContent } from '../styles'
import { AdminSubscriptionTableRow, formatCurrency } from '../utils'

interface Props {
  row: AdminSubscriptionTableRow
}

export const SubscriptionTransactions: React.FC<Props> = ({ row }) => (
  <ExpandedContent>
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      spacing={1}
      sx={{ mb: 1.5 }}
    >
      <div>
        <Typography variant="subtitle2">
          Transactions for subscription #{row.subscriptionId}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {row.transactionCount > 0
            ? 'Recurring payment history associated with this rental.'
            : 'No linked charges have been matched to this subscription yet.'}
        </Typography>
      </div>
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Chip
          size="small"
          label={formatCurrency(
            row.transactions.reduce(
              (accumulator, transaction) =>
                accumulator + transaction.settleAmount,
              0
            )
          )}
        />
        <Chip
          size="small"
          label={`${row.transactionCount} charges`}
          color="primary"
          variant="outlined"
        />
      </Stack>
    </Stack>
    {row.transactions.length === 0 ? (
      <EmptyTransactionsAlert severity="info">
        No linked charges were found for this subscription in the current
        customer history or recurring billing activity.
      </EmptyTransactionsAlert>
    ) : (
      <Table size="small" aria-label={`transactions for ${row.subscriptionId}`}>
        <TableHead>
          <TableRow>
            <TableCell>Transaction</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Account</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {row.transactions.map((transaction) => (
            <TableRow key={transaction.transId}>
              <TableCell>
                <Stack spacing={0.25}>
                  <Typography variant="body2">
                    #{transaction.transId}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {transaction.invoiceNumber || 'No invoice reference'}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell>{transaction.submitTimeLocal}</TableCell>
              <TableCell>
                <Stack spacing={0.25}>
                  <Typography variant="body2">
                    {transaction.accountNumber}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {transaction.accountType}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell align="right">
                {formatCurrency(transaction.settleAmount)}
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={transaction.transactionStatus}
                  color={getTransactionStatusChipColor(
                    transaction.transactionStatus
                  )}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )}
  </ExpandedContent>
)
